import React from 'react';
import {
  Box,
  IconButton,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon,
  Tag,
  Layers,
  Inventory,
  Money,
  LocalShipping,
  CheckCircle,
  Upload,
  ArrowRight,
  DeleteOutline,
  Edit,
  Add,
} from '@mui/icons-material';
import { DecimalNumberInput } from '../../ui';
import { ProductTag } from '../../../types/product.types';
import { APP_CURRENCY } from '../../../utils/constants/currency';
import { useProductDrawerEdit } from './ProductDrawerEdit.hooks';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { useSlidePanelAnimation } from '../../../hooks/useSlidePanelAnimation';
import ImageModal from '../../ui/ImageModal';
import AddMaterialModal from '../ProductDrawer/AddMaterialModal';
import AddColorModal from '../ProductDrawer/AddColorModal';
import AddSupplierModal from '../ProductDrawer/AddSupplierModal';
import AddCategoryModal from '../ProductDrawer/AddCategoryModal';
import AddTagModal from '../ProductDrawer/AddTagModal';
import { CloseConfirmationModal } from '../../modals/confirmation';
import { ConfirmationModal as GlobalConfirmationModal } from '../../ui/Modal';
import {
  PanelOverlay,
  PanelContainer,
  DrawerContainer,
  DrawerHeader,
  DrawerTitle,
  CloseButton,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  ProgressText,
  StepIndicatorContainer,
  StepIndicator,
  StepItem,
  StepCircle,
  StepLabel,
  StepContentContainer,
  StepCard,
  StepCardHeader,
  StepCardHeaderBorder,
  StepCardHeaderIcon,
  StepCardTitle,
  StepCardContent,
  FormField,
  FormLabel,
  StyledTextField,
  StyledSelect,
  ImageUploadContainer,
  ImagePreview,
  ImageActions,
  ImageActionButton,
  UploadButton,
  UploadText,
  TagChip,
  FooterContainer,
  FooterActions,
  FooterLeft,
  FooterRight,
  PrimaryActionButton,
  SecondaryActionButton,
  LoadingOverlay,
  ErrorAlert,
  HelperText,
  PRODUCT_DRAWER_COLORS,
  PRODUCT_STEPS,
  PANEL_FOOTER_GRADIENT,
} from './ProductDrawerEdit.styles';
import { CATALOG_CTA_GRADIENT } from '../catalogGradients';
import { useCatalogAutocompleteOpen } from '../useCatalogAutocompleteOpen';
import type { CatalogTagRow } from '../../../utils/catalogPageFetchers';

export interface ProductDrawerEditProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: any) => Promise<void>;
  productTypes: { id: number; name: string }[];
  productTags: ProductTag[];
  productMaterials: { id: number; name: string }[];
  productColors: { id: number; name: string }[];
  productSuppliers: { id: number; name: string }[];
  editProduct: any | null;
  onCategoryAdded?: (newCategoryId: number) => void;
  onTagAdded?: (newTagId: number) => void;
  onMaterialAdded?: (newMaterialId: number) => void;
  onColorAdded?: (newColorId: number) => void;
  onSupplierAdded?: (newSupplierId: number) => void;
}

const ProductDrawerEdit: React.FC<ProductDrawerEditProps> = ({
  isOpen,
  onClose,
  onSave,
  productTypes,
  productTags,
  productMaterials,
  productColors,
  productSuppliers,
  editProduct = null,
  onCategoryAdded,
  onTagAdded,
  onMaterialAdded,
  onColorAdded,
  onSupplierAdded,
}) => {
  const {
    currentStep,
    formProgress,
    goNext,
    goPrev,
    goToStep,
    formData,
    setFormData,
    selectedTags,
    imageUrl,
    imageError,
    isUploading,
    fileInputRef,
    showImageModal,
    setShowImageModal,
    showConfirmation,
    setShowConfirmation,
    showCancelConfirmation,
    setShowCancelConfirmation,
    isSaving,
    tagError,
    showAddMaterialModal,
    handleOpenAddMaterialModal,
    handleCloseAddMaterialModal,
    handleSaveMaterial,
    showAddColorModal,
    handleOpenAddColorModal,
    handleCloseAddColorModal,
    handleSaveColor,
    showAddCategoryModal,
    handleOpenAddCategoryModal,
    handleCloseAddCategoryModal,
    handleSaveCategory,
    categoryCatalog,
    tagsCatalog,
    supplierCatalog,
    showAddSupplierModal,
    handleOpenAddSupplierModal,
    handleCloseAddSupplierModal,
    handleSaveSupplier,
    showAddTagModal,
    handleOpenAddTagModal,
    handleCloseAddTagModal,
    handleSaveTag,
    materialCatalog,
    colorCatalog,
    handleInputChange,
    handleSelectChange,
    handleSelectedTagsChange,
    handleImageSelect,
    handleImageChange,
    handleRemoveImage,
    handleImageClick,
    validateCurrentStep,
    handleSave,
    handleCloseClick,
    cancelOperation,
  } = useProductDrawerEdit(
    isOpen,
    onClose,
    onSave,
    editProduct,
    onCategoryAdded,
    onTagAdded,
    productTags,
    productTypes,
    productMaterials,
    productColors,
    onMaterialAdded,
    onColorAdded,
    productSuppliers,
    onSupplierAdded,
  );

  // Interceptar ESC para mostrar confirmación de cierre
  useEscapeKey(
    () => {
      if (isOpen && !isSaving && !showCancelConfirmation) {
        handleCloseClick();
      }
    },
    isOpen && !isSaving && !showCancelConfirmation,
  );

  const isCatalogModalOpen =
    showAddCategoryModal ||
    showAddTagModal ||
    showAddMaterialModal ||
    showAddColorModal ||
    showAddSupplierModal;
  const { getAutocompleteOpenProps, closeAutocompleteAndOpenModal } =
    useCatalogAutocompleteOpen(isCatalogModalOpen);

  // Mount through close animation (sibling overlay + panel shell)
  const { shouldRender, panelClassName } = useSlidePanelAnimation(isOpen);

  const colors = PRODUCT_DRAWER_COLORS;
  const steps = React.useMemo(
    () => [
      {
        id: 1,
        name: 'Información Básica',
        icon: Tag,
        color: PRODUCT_STEPS[0].color,
        completedBg: PRODUCT_STEPS[0].completedBg,
        completedFg: PRODUCT_STEPS[0].completedFg,
      },
      {
        id: 2,
        name: 'Categoría',
        icon: Layers,
        color: PRODUCT_STEPS[1].color,
        completedBg: PRODUCT_STEPS[1].completedBg,
        completedFg: PRODUCT_STEPS[1].completedFg,
      },
      {
        id: 3,
        name: 'Propiedades',
        icon: Inventory,
        color: PRODUCT_STEPS[2].color,
        completedBg: PRODUCT_STEPS[2].completedBg,
        completedFg: PRODUCT_STEPS[2].completedFg,
      },
      {
        id: 4,
        name: 'Precios',
        icon: Money,
        color: PRODUCT_STEPS[3].color,
        completedBg: PRODUCT_STEPS[3].completedBg,
        completedFg: PRODUCT_STEPS[3].completedFg,
      },
      {
        id: 5,
        name: 'Proveedor',
        icon: LocalShipping,
        color: PRODUCT_STEPS[4].color,
        completedBg: PRODUCT_STEPS[4].completedBg,
        completedFg: PRODUCT_STEPS[4].completedFg,
      },
    ],
    [],
  );

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <StepCardHeader color={colors.violetSoft}>
              <StepCardHeaderBorder color={colors.violetSoft} />
              <StepCardHeaderIcon color={colors.violetSoft}>
                <Tag />
              </StepCardHeaderIcon>
              <StepCardTitle>Información Básica</StepCardTitle>
            </StepCardHeader>
            <StepCard>
              <StepCardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Nombre del Producto *</FormLabel>
                      <StyledTextField
                        fullWidth
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        variant="outlined"
                        size="small"
                        placeholder="Ej. Anillo de oro"
                        required
                      />
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Código de catálogo</FormLabel>
                      <StyledTextField
                        fullWidth
                        name="code"
                        value={formData.code}
                        variant="outlined"
                        size="small"
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                      <HelperText sx={{ mt: 0 }}>
                        No editable; se usa para inventario y etiquetas (formato numérico o ART-).
                      </HelperText>
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Estado *</FormLabel>
                      <StyledSelect fullWidth size="small">
                        <Select name="status" value={formData.status} onChange={handleSelectChange}>
                          <MenuItem value="ACTIVE">Activo</MenuItem>
                          <MenuItem value="INACTIVE">Inactivo</MenuItem>
                        </Select>
                      </StyledSelect>
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Imagen del Producto</FormLabel>
                      <ImageUploadContainer>
                        {isUploading && (
                          <LoadingOverlay>
                            <CircularProgress size={40} sx={{ color: 'white' }} />
                          </LoadingOverlay>
                        )}
                        {imageUrl ? (
                          <ImagePreview>
                            <img
                              src={imageUrl}
                              alt="Vista previa del producto"
                              onClick={handleImageClick}
                              style={{ cursor: 'pointer' }}
                            />
                            <ImageActions>
                              <ImageActionButton
                                bgColor={colors.cyanButton}
                                onClick={handleImageSelect}
                                size="small"
                              >
                                <Edit fontSize="small" />
                              </ImageActionButton>
                              <ImageActionButton
                                gradient={CATALOG_CTA_GRADIENT}
                                onClick={handleRemoveImage}
                                size="small"
                              >
                                <DeleteOutline fontSize="small" />
                              </ImageActionButton>
                            </ImageActions>
                          </ImagePreview>
                        ) : (
                          <>
                            <Upload sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                            <UploadText>Arrastra una imagen o haz clic para seleccionar</UploadText>
                            <UploadButton
                              gradient={`linear-gradient(to right, ${colors.cyanButton}, ${colors.greenButton})`}
                              onClick={handleImageSelect}
                            >
                              Seleccionar archivo
                            </UploadButton>
                          </>
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageChange}
                          accept=".jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                        />
                      </ImageUploadContainer>
                      {imageError && <ErrorAlert severity="error">{imageError}</ErrorAlert>}
                      <HelperText>
                        Formatos permitidos: JPG, JPEG, PNG. Tamaño máximo: 2MB
                      </HelperText>
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Descripción</FormLabel>
                      <StyledTextField
                        fullWidth
                        multiline
                        rows={4}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        variant="outlined"
                        size="small"
                        placeholder="Describe las características del producto..."
                      />
                    </FormField>
                  </Grid>
                </Grid>
              </StepCardContent>
            </StepCard>
          </>
        );

      case 2:
        return (
          <>
            <StepCardHeader color={colors.blueRoyal}>
              <StepCardHeaderBorder color={colors.blueRoyal} />
              <StepCardHeaderIcon color={colors.blueRoyal}>
                <Layers />
              </StepCardHeaderIcon>
              <StepCardTitle>Categoría</StepCardTitle>
            </StepCardHeader>
            <StepCard>
              <StepCardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Tipo de Producto</FormLabel>
                      <Autocomplete
                        {...getAutocompleteOpenProps('category', categoryCatalog.onOpen)}
                        options={categoryCatalog.displayOptions}
                        filterOptions={categoryCatalog.filterOptions}
                        loading={categoryCatalog.loading}
                        openOnFocus
                        onInputChange={categoryCatalog.onInputChange}
                        getOptionLabel={(option) => option.name}
                        value={
                          categoryCatalog.displayOptions.find(
                            (t) => t.id.toString() === formData.type,
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          handleSelectChange({
                            target: {
                              name: 'type',
                              value: newValue ? newValue.id.toString() : '',
                            },
                          });
                        }}
                        size="small"
                        ListboxProps={categoryCatalog.listboxProps}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Buscar tipo de producto..."
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <IconButton
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeAutocompleteAndOpenModal(handleOpenAddCategoryModal);
                                    }}
                                    edge="start"
                                    size="small"
                                    aria-label="Agregar nuevo tipo de producto"
                                    sx={{
                                      mr: 0.75,
                                      width: 24,
                                      height: 24,
                                      minWidth: 24,
                                      color: 'white',
                                      borderRadius: '50%',
                                      background: `linear-gradient(135deg, ${colors.cyanButton} 0%, ${colors.greenButton} 100%)`,
                                      '&:hover': {
                                        background: `linear-gradient(135deg, #0891b2 0%, #059669 100%)`,
                                      },
                                    }}
                                  >
                                    <Add sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                '& fieldset': {
                                  borderColor: 'rgba(12, 187, 154, 0.45)',
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.success,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#0BB296',
                                  boxShadow: `0 0 0 3px ${colors.success}33`,
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Etiquetas</FormLabel>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Autocomplete
                            {...getAutocompleteOpenProps('tags', tagsCatalog.onOpen)}
                            multiple
                            options={tagsCatalog.displayOptions}
                            filterOptions={tagsCatalog.filterOptions}
                            loading={tagsCatalog.loading}
                            openOnFocus
                            onInputChange={tagsCatalog.onInputChange}
                            getOptionLabel={(option) => option.name}
                            value={selectedTags as CatalogTagRow[]}
                            onChange={(event, newValue) => handleSelectedTagsChange(newValue)}
                            isOptionEqualToValue={(option, value) =>
                              (option.id != null && value.id != null && option.id === value.id) ||
                              option.name.toLowerCase() === value.name.toLowerCase()
                            }
                            size="small"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Seleccionar etiquetas..."
                                variant="outlined"
                                error={!!tagError}
                                helperText={tagError}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <IconButton
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          closeAutocompleteAndOpenModal(handleOpenAddTagModal);
                                        }}
                                        edge="start"
                                        size="small"
                                        aria-label="Agregar nueva etiqueta"
                                        sx={{
                                          mr: 0.75,
                                          width: 24,
                                          height: 24,
                                          minWidth: 24,
                                          color: 'white',
                                          borderRadius: '50%',
                                          background: `linear-gradient(135deg, ${colors.cyanButton} 0%, ${colors.greenButton} 100%)`,
                                          '&:hover': {
                                            background: `linear-gradient(135deg, #0891b2 0%, #059669 100%)`,
                                          },
                                        }}
                                      >
                                        <Add sx={{ fontSize: 16 }} />
                                      </IconButton>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    '& fieldset': {
                                      borderColor: 'rgba(12, 187, 154, 0.45)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: colors.success,
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#0BB296',
                                      boxShadow: `0 0 0 3px ${colors.success}33`,
                                    },
                                  },
                                }}
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const tagColor = option.color || '#3b82f6';
                                return (
                                  <TagChip
                                    {...getTagProps({ index })}
                                    key={option.id ?? option.name}
                                    label={option.name}
                                    size="small"
                                    sx={{
                                      backgroundColor: `${tagColor}20`,
                                      color: tagColor,
                                      border: `1px solid ${tagColor}40`,
                                      '& .MuiChip-deleteIcon': {
                                        color: tagColor,
                                      },
                                    }}
                                  />
                                );
                              })
                            }
                            ListboxProps={tagsCatalog.listboxProps}
                          />
                        </Box>
                      </Box>
                    </FormField>
                  </Grid>
                </Grid>
              </StepCardContent>
            </StepCard>
          </>
        );

      case 3:
        return (
          <>
            <StepCardHeader color={colors.cyanButton}>
              <StepCardHeaderBorder color={colors.cyanButton} />
              <StepCardHeaderIcon color={colors.cyanButton}>
                <Inventory />
              </StepCardHeaderIcon>
              <StepCardTitle>Propiedades</StepCardTitle>
            </StepCardHeader>
            <StepCard>
              <StepCardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Material</FormLabel>
                      <Autocomplete
                        {...getAutocompleteOpenProps('material', materialCatalog.onOpen)}
                        options={materialCatalog.displayOptions}
                        filterOptions={materialCatalog.filterOptions}
                        loading={materialCatalog.loading}
                        openOnFocus
                        onInputChange={materialCatalog.onInputChange}
                        getOptionLabel={(option) => option.name}
                        value={
                          materialCatalog.displayOptions.find(
                            (m) => m.id.toString() === formData.material,
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          handleSelectChange({
                            target: {
                              name: 'material',
                              value: newValue ? newValue.id.toString() : '',
                            },
                          });
                        }}
                        size="small"
                        ListboxProps={materialCatalog.listboxProps}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Buscar material..."
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <IconButton
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeAutocompleteAndOpenModal(handleOpenAddMaterialModal);
                                    }}
                                    edge="start"
                                    size="small"
                                    aria-label="Agregar nuevo material"
                                    sx={{
                                      mr: 0.75,
                                      width: 24,
                                      height: 24,
                                      minWidth: 24,
                                      color: 'white',
                                      borderRadius: '50%',
                                      background: `linear-gradient(135deg, ${colors.cyanButton} 0%, ${colors.greenButton} 100%)`,
                                      '&:hover': {
                                        background: `linear-gradient(135deg, #0891b2 0%, #059669 100%)`,
                                      },
                                    }}
                                  >
                                    <Add sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                '& fieldset': { borderColor: 'rgba(12, 187, 154, 0.45)' },
                                '&:hover fieldset': { borderColor: colors.success },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#0BB296',
                                  boxShadow: `0 0 0 3px ${colors.success}33`,
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Peso (g)</FormLabel>
                      <DecimalNumberInput
                        fullWidth
                        name="weight"
                        value={formData.weight}
                        onChange={(val) =>
                          setFormData((prev: any) => ({
                            ...prev,
                            weight: val != null ? String(val) : '',
                          }))
                        }
                        variant="outlined"
                        size="small"
                        placeholder="Ej. 0,5"
                        decimalPlaces={2}
                        min={0}
                      />
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Largo (cm)</FormLabel>
                      <DecimalNumberInput
                        fullWidth
                        name="length"
                        value={formData.length}
                        onChange={(val) =>
                          setFormData((prev: any) => ({
                            ...prev,
                            length: val != null ? String(val) : '',
                          }))
                        }
                        variant="outlined"
                        size="small"
                        placeholder="Ej. 30"
                        decimalPlaces={2}
                        min={0}
                      />
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Talla</FormLabel>
                      <StyledTextField
                        fullWidth
                        name="diameter"
                        value={formData.diameter}
                        onChange={handleInputChange}
                        variant="outlined"
                        size="small"
                        placeholder="Ej. 20cm, XL, 42"
                      />
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Color</FormLabel>
                      <Autocomplete
                        {...getAutocompleteOpenProps('color', colorCatalog.onOpen)}
                        options={colorCatalog.displayOptions}
                        filterOptions={colorCatalog.filterOptions}
                        loading={colorCatalog.loading}
                        openOnFocus
                        onInputChange={colorCatalog.onInputChange}
                        getOptionLabel={(option) => option.name}
                        value={
                          colorCatalog.displayOptions.find(
                            (c) => c.id.toString() === formData.colorId,
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          handleSelectChange({
                            target: {
                              name: 'colorId',
                              value: newValue ? newValue.id.toString() : '',
                            },
                          });
                          setFormData((prev: any) => ({
                            ...prev,
                            color: newValue ? newValue.name : '',
                          }));
                        }}
                        size="small"
                        ListboxProps={colorCatalog.listboxProps}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Buscar color..."
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <IconButton
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeAutocompleteAndOpenModal(handleOpenAddColorModal);
                                    }}
                                    edge="start"
                                    size="small"
                                    aria-label="Agregar nuevo color"
                                    sx={{
                                      mr: 0.75,
                                      width: 24,
                                      height: 24,
                                      minWidth: 24,
                                      color: 'white',
                                      borderRadius: '50%',
                                      background: `linear-gradient(135deg, ${colors.cyanButton} 0%, ${colors.greenButton} 100%)`,
                                      '&:hover': {
                                        background: `linear-gradient(135deg, #0891b2 0%, #059669 100%)`,
                                      },
                                    }}
                                  >
                                    <Add sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                '& fieldset': { borderColor: 'rgba(12, 187, 154, 0.45)' },
                                '&:hover fieldset': { borderColor: colors.success },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#0BB296',
                                  boxShadow: `0 0 0 3px ${colors.success}33`,
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Código Proveedor</FormLabel>
                      <StyledTextField
                        fullWidth
                        name="workshopCode"
                        value={formData.workshopCode}
                        onChange={handleInputChange}
                        variant="outlined"
                        size="small"
                        placeholder="Ej. TALL-001"
                      />
                    </FormField>
                  </Grid>
                </Grid>
              </StepCardContent>
            </StepCard>
          </>
        );

      case 4:
        return (
          <>
            <StepCardHeader color={colors.tealMedium}>
              <StepCardHeaderBorder color={colors.tealMedium} />
              <StepCardHeaderIcon color={colors.tealMedium}>
                <Money />
              </StepCardHeaderIcon>
              <StepCardTitle>Precios</StepCardTitle>
            </StepCardHeader>
            <StepCard>
              <StepCardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Costo Unitario *</FormLabel>
                      <DecimalNumberInput
                        fullWidth
                        name="costPrice"
                        value={formData.costPrice}
                        onChange={(val) =>
                          setFormData((prev: any) => ({
                            ...prev,
                            costPrice: val != null ? String(val) : '',
                          }))
                        }
                        variant="outlined"
                        size="small"
                        placeholder="0,00"
                        decimalPlaces={2}
                        min={0.01}
                        max={999999.99}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">{APP_CURRENCY.symbol}</InputAdornment>
                          ),
                        }}
                        required
                      />
                      <HelperText color="warning.main">
                        Solo se permiten hasta 2 decimales
                      </HelperText>
                    </FormField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Precio de Venta *</FormLabel>
                      <DecimalNumberInput
                        fullWidth
                        name="sellingPrice"
                        value={formData.sellingPrice}
                        onChange={(val) =>
                          setFormData((prev: any) => ({
                            ...prev,
                            sellingPrice: val != null ? String(val) : '',
                          }))
                        }
                        variant="outlined"
                        size="small"
                        placeholder="0,00"
                        decimalPlaces={2}
                        min={0.01}
                        max={999999.99}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">{APP_CURRENCY.symbol}</InputAdornment>
                          ),
                        }}
                        required
                      />
                      <HelperText color="warning.main">
                        Solo se permiten hasta 2 decimales
                      </HelperText>
                    </FormField>
                  </Grid>
                </Grid>
              </StepCardContent>
            </StepCard>
          </>
        );

      case 5:
        return (
          <>
            <StepCardHeader color={colors.greenButton}>
              <StepCardHeaderBorder color={colors.greenButton} />
              <StepCardHeaderIcon color={colors.greenButton}>
                <LocalShipping />
              </StepCardHeaderIcon>
              <StepCardTitle>Proveedor</StepCardTitle>
            </StepCardHeader>
            <StepCard>
              <StepCardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormField>
                      <FormLabel>Proveedor</FormLabel>
                      <Autocomplete
                        {...getAutocompleteOpenProps('supplier', supplierCatalog.onOpen)}
                        options={supplierCatalog.displayOptions}
                        filterOptions={supplierCatalog.filterOptions}
                        loading={supplierCatalog.loading}
                        openOnFocus
                        onInputChange={supplierCatalog.onInputChange}
                        getOptionLabel={(option) => option.name}
                        value={
                          supplierCatalog.displayOptions.find(
                            (s) => s.id.toString() === formData.supplier,
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          handleSelectChange({
                            target: {
                              name: 'supplier',
                              value: newValue ? newValue.id.toString() : '',
                            },
                          });
                        }}
                        size="small"
                        ListboxProps={supplierCatalog.listboxProps}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Buscar proveedor..."
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <IconButton
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeAutocompleteAndOpenModal(handleOpenAddSupplierModal);
                                    }}
                                    edge="start"
                                    size="small"
                                    aria-label="Agregar nuevo proveedor"
                                    sx={{
                                      mr: 0.75,
                                      width: 24,
                                      height: 24,
                                      minWidth: 24,
                                      color: 'white',
                                      borderRadius: '50%',
                                      background: `linear-gradient(135deg, ${colors.cyanButton} 0%, ${colors.greenButton} 100%)`,
                                      '&:hover': {
                                        background: `linear-gradient(135deg, #0891b2 0%, #059669 100%)`,
                                      },
                                    }}
                                  >
                                    <Add sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                '& fieldset': { borderColor: 'rgba(12, 187, 154, 0.45)' },
                                '&:hover fieldset': { borderColor: colors.success },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#0BB296',
                                  boxShadow: `0 0 0 3px ${colors.success}33`,
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </FormField>
                  </Grid>
                </Grid>
              </StepCardContent>
            </StepCard>
          </>
        );

      default:
        return null;
    }
  };

  // Render step indicator
  const renderStepIndicator = () => {
    return (
      <StepIndicator>
        {steps.map((step) => {
          const StepIcon = step.icon;
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;

          return (
            <StepItem key={step.id} onClick={() => goToStep(step.id)} sx={{ cursor: 'pointer' }}>
              <StepCircle
                isActive={isActive}
                isCompleted={isCompleted}
                color={step.color}
                completedBg={step.completedBg}
                completedFg={step.completedFg}
              >
                <StepIcon />
              </StepCircle>
              <StepLabel isActive={isActive} isCompleted={isCompleted}>
                {step.name}
              </StepLabel>
            </StepItem>
          );
        })}
      </StepIndicator>
    );
  };

  return (
    <>
      <GlobalConfirmationModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleSave}
        title="Confirmar acción"
        message="¿Confirmas guardar este producto en el catálogo?"
        confirmText="Confirmar"
        cancelText="Volver"
        variant="default"
        icon={<CheckCircle />}
        loading={isSaving}
      />
      <CloseConfirmationModal
        open={showCancelConfirmation}
        onClose={() => setShowCancelConfirmation(false)}
        onConfirm={cancelOperation}
      />
      {shouldRender ? (
        <>
          {/* Backdrop is a sibling (not a parent) so the panel stays fully opaque while overlay fades. */}
          <PanelOverlay className={panelClassName} />
          <PanelContainer className={panelClassName}>
            <DrawerContainer>
              <DrawerHeader>
                <DrawerTitle>
                  {editProduct?.code != null && editProduct.code !== ''
                    ? `Editar Producto ${editProduct.code}`
                    : 'Editar Producto'}
                </DrawerTitle>
                <CloseButton onClick={handleCloseClick} aria-label="Cerrar panel">
                  <CloseIcon sx={{ fontSize: 18 }} />
                </CloseButton>
              </DrawerHeader>

              <ProgressContainer>
                <ProgressBar>
                  <ProgressFill sx={{ width: `${formProgress}%` }} />
                </ProgressBar>
                <ProgressText>
                  <span>
                    Paso {currentStep} de {steps.length}
                  </span>
                  <span>{formProgress}% completado</span>
                </ProgressText>
              </ProgressContainer>

              <StepIndicatorContainer>{renderStepIndicator()}</StepIndicatorContainer>

              <StepContentContainer>{renderStepContent()}</StepContentContainer>

              <FooterContainer>
                <FooterActions>
                  <FooterLeft>
                    {currentStep > 1 && (
                      <SecondaryActionButton onClick={goPrev}>Anterior</SecondaryActionButton>
                    )}
                  </FooterLeft>
                  <FooterRight>
                    {currentStep === steps.length ? (
                      <PrimaryActionButton
                        gradient={PANEL_FOOTER_GRADIENT}
                        onClick={() => {
                          if (!isSaving) {
                            setShowConfirmation(true);
                          }
                        }}
                        disabled={!validateCurrentStep() || isSaving}
                      >
                        Guardar Producto
                      </PrimaryActionButton>
                    ) : (
                      <PrimaryActionButton
                        gradient={PANEL_FOOTER_GRADIENT}
                        onClick={goNext}
                        disabled={!validateCurrentStep()}
                        endIcon={<ArrowRight />}
                      >
                        Siguiente
                      </PrimaryActionButton>
                    )}
                  </FooterRight>
                </FooterActions>
              </FooterContainer>
            </DrawerContainer>

            <ImageModal
              isOpen={showImageModal}
              onClose={() => setShowImageModal(false)}
              imageUrl={imageUrl}
              altText="Imagen del producto"
            />

            <AddCategoryModal
              isOpen={showAddCategoryModal}
              onClose={handleCloseAddCategoryModal}
              onSave={handleSaveCategory}
            />
            <AddMaterialModal
              isOpen={showAddMaterialModal}
              onClose={handleCloseAddMaterialModal}
              onSave={handleSaveMaterial}
            />
            <AddColorModal
              isOpen={showAddColorModal}
              onClose={handleCloseAddColorModal}
              onSave={handleSaveColor}
            />
            <AddSupplierModal
              isOpen={showAddSupplierModal}
              onClose={handleCloseAddSupplierModal}
              onSave={handleSaveSupplier}
            />
            <AddTagModal
              isOpen={showAddTagModal}
              onClose={handleCloseAddTagModal}
              onSave={handleSaveTag}
            />
          </PanelContainer>
        </>
      ) : null}
    </>
  );
};

export default ProductDrawerEdit;
